import { observer } from "mobx-react-lite";
import { message, Spin } from "antd";
import type { LobbyModel } from "./LobbyModel";
import type { InfluencerCard } from "./LobbyType";
import {
  type ListTab,
  cornerLabel,
  filterCards,
} from "./relatedSpacesFollowsLogic";
import "./LobbyStyle.css";

const CREATOR_APPLY_PATH = "/creator-apply";

const getCreatorApplyUrl = () => {
  const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${window.location.origin}${publicUrl}${CREATOR_APPLY_PATH}`;
};

type Props = {
  model: LobbyModel;
  listTab: ListTab;
  onTabChange: (t: ListTab) => void;
  onCardNavigate: (targetSpaceId: string) => void;
  onEnterSpaceIdHome: () => void;
  /** 是否在顶部展示与大页相同的「与我相关的空间」标题（弹框仅用筛选行时可关） */
  showPageHeading?: boolean;
};

export const RelatedSpacesFollowsPanel = observer((props: Props) => {
  const {
    model,
    listTab,
    onTabChange,
    onCardNavigate,
    onEnterSpaceIdHome,
    showPageHeading = true,
  } = props;

  const onCopyInviteLink = async () => {
    const url = getCreatorApplyUrl();
    try {
      await navigator.clipboard.writeText(url);
      message.success("已复制到剪贴板");
    } catch {
      message.error("复制失败，请稍后重试");
    }
  };

  const displayCards = filterCards(model.cards, listTab);
  const rawCardCount = model.cards.length;

  const showFollowsEmpty =
    model.loaded === true && !model.loadError && rawCardCount === 0;

  const showFilterEmpty =
    model.loaded === true &&
    !model.loadError &&
    rawCardCount > 0 &&
    displayCards.length === 0;

  const emptyCopy =
    listTab === "sub"
      ? { line: "你尚未订阅任何空间的服务。", showEnterId: false }
      : {
          line: "暂无空间，快去抖音/视频号上让你关注的大V来入驻吧！",
          showEnterId: true,
        };

  return (
    <div className="lobby-body lobby-body--subscriptions follows-panel-body">
      {showPageHeading ? (
        <div className="lobby-follows-header">
          <h1 className="lobby-page-title">与我相关的空间</h1>
        </div>
      ) : null}
      <div
        className="lobby-filter-row follows-panel-filter"
        role="tablist"
        aria-label="空间列表筛选项"
      >
        {(
          [
            { key: "all" as const, label: "全部" },
            { key: "follow" as const, label: "我的关注" },
            { key: "sub" as const, label: "我的订阅" },
          ] as const
        ).map((opt, i) => (
          <span key={opt.key} className="lobby-filter-item">
            {i > 0 ? <span className="lobby-filter-sep"> </span> : null}
            <button
              type="button"
              role="tab"
              className={[
                "lobby-filter-text",
                listTab === opt.key ? "lobby-filter-text--on" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => onTabChange(opt.key)}
            >
              {opt.label}
            </button>
          </span>
        ))}
      </div>
      <Spin spinning={!model.loaded}>
        {model.loadError && <p className="lobby-error">{model.loadError}</p>}
        {showFollowsEmpty ? (
          <div className="lobby-empty">
            <div className="lobby-empty-panel">
              <p className="lobby-empty-line">{emptyCopy.line}</p>
              {emptyCopy.showEnterId ? (
                <div className="lobby-empty-actions">
                  <button
                    type="button"
                    className="lobby-empty-id-btn"
                    onClick={() => void onCopyInviteLink()}
                  >
                    复制邀请链接
                  </button>
                  <button
                    type="button"
                    className="lobby-empty-id-btn"
                    onClick={onEnterSpaceIdHome}
                  >
                    输入ID进入空间
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : showFilterEmpty ? (
          <p className="lobby-filter-empty" role="status">
            {listTab === "follow"
              ? "暂无已点「关注」的大 V 空间；仅已建订阅/会员关系的请在「我的订阅」或「全部」查看。"
              : "当前筛选下暂无相关空间"}
          </p>
        ) : (
          <ul className="lobby-grid">
            {displayCards.map((c: InfluencerCard) => {
              const cr = cornerLabel(c.membershipTag);
              return (
                <li key={c.id} className="lobby-item">
                  <button
                    type="button"
                    className="lobby-card"
                    onClick={() => onCardNavigate(c.targetSpaceId)}
                  >
                    {cr ? (
                      <span className="lobby-card-corner" aria-hidden>
                        {cr}
                      </span>
                    ) : null}
                    <div className="lobby-card-media">
                      <img
                        className="lobby-card-img"
                        src={c.coverImageUrl}
                        alt=""
                        loading="lazy"
                      />
                      <div className="lobby-card-foot" aria-hidden>
                        <span className="lobby-card-name">{c.name}</span>
                        <span className="lobby-card-id">{c.targetSpaceId}</span>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Spin>
    </div>
  );
});
